import React, { useState, useRef, useEffect } from 'react';
import { useFamilyContext } from '../../../contexts/FamilyContext';
import { supabaseService } from '../../../lib/supabase-service';
import { X, Plane, Car, Ship, Train, MapPin, Calendar, DollarSign, Users, Clock, Save, ChevronLeft, ChevronRight, Upload, Globe, Mountain, Building, Umbrella, TreePine, Waves, Home, Hotel, Ticket, Plus, Trash2, Image } from 'lucide-react';
import TripBudgetCalculator from './TripBudgetCalculator';

export default function TripForm({ trip = null, onClose, mode = 'add' }) {
  const { addFamilyActivity, updateFamilyActivity, currentUser, family } = useFamilyContext();
  const [activeTab, setActiveTab] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  
  // Calculate number of family members
  const familyMembers = [...(family?.parents || []), ...(family?.kids || [])];
  // Using 6 for expense calculations (excluding test account)
  const numberOfPeople = 6;
  
  // Form state
  const [formData, setFormData] = useState({
    // Basic Info
    title: trip?.title || '',
    location: trip?.location || '',
    description: trip?.description || '',
    
    // Trip Type & Duration
    transport_type: trip?.trip_metadata?.transport_type || 'drive',
    duration_category: trip?.trip_metadata?.duration_category || 'weekend',
    travel_time: trip?.trip_metadata?.travel_time || '',
    
    // Trip Features
    trip_features: trip?.trip_metadata?.trip_features || [],
    
    // Timing
    preferred_season: trip?.trip_metadata?.preferred_season || 'any',
    preferred_months: trip?.trip_metadata?.preferred_months || [],
    
    // Images
    images: trip?.trip_metadata?.images || {
      main: null,
      secondary: null,
      tertiary: null
    },
    
    // Budget
    budget_breakdown: trip?.trip_metadata?.budget_breakdown || null,
    
    // Trip Phase
    trip_phase: trip?.trip_metadata?.trip_phase || 'idea',
    
    // International
    passport_required: trip?.trip_metadata?.passport_required || false,
    visa_required: trip?.trip_metadata?.visa_required || false,
    
    // Accommodations (multiple lodging ideas)
    accommodations: trip?.trip_metadata?.accommodations || [],
    
    // Activities (things to do)
    trip_activities: trip?.trip_metadata?.trip_activities || []
  });
  
  const [uploadingImage, setUploadingImage] = useState(false);
  const [currentImageSlot, setCurrentImageSlot] = useState('main');
  
  // Transport options
  const transportOptions = [
    { value: 'drive', label: 'Drive', icon: Car },
    { value: 'fly', label: 'Fly', icon: Plane },
    { value: 'cruise', label: 'Cruise', icon: Ship },
    { value: 'train', label: 'Train', icon: Train }
  ];
  
  // Duration options
  const durationOptions = [
    { value: 'overnight', label: 'Overnight' },
    { value: 'weekend', label: '2-3 days' },
    { value: '4-7days', label: '4-7 days' },
    { value: '1-2weeks', label: '1-2 weeks' },
    { value: '2+weeks', label: '2+ weeks' }
  ];
  
  // Trip features
  const tripFeatures = [
    { value: 'domestic', label: 'Domestic', icon: '🇺🇸' },
    { value: 'international', label: 'International', icon: '🌍' },
    { value: 'coastal', label: 'Coastal/Beach', icon: '🏖️' },
    { value: 'mountains', label: 'Mountains', icon: '⛰️' },
    { value: 'desert', label: 'Desert', icon: '🏜️' },
    { value: 'city', label: 'City/Urban', icon: '🏙️' },
    { value: 'rural', label: 'Rural/Countryside', icon: '🌾' },
    { value: 'theme_parks', label: 'Theme Parks', icon: '🎢' },
    { value: 'national_parks', label: 'National Parks', icon: '🏞️' },
    { value: 'resort', label: 'Resort/All-Inclusive', icon: '🏨' },
    { value: 'adventure', label: 'Adventure', icon: '🎯' },
    { value: 'relaxation', label: 'Relaxation', icon: '🧘' },
    { value: 'cultural', label: 'Cultural/Historical', icon: '🎭' },
    { value: 'food_focused', label: 'Food & Culinary', icon: '🍽️' }
  ];
  
  // Seasons
  const seasons = ['Spring', 'Summer', 'Fall', 'Winter', 'Any'];
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];
  
  // Toggle trip feature
  const toggleFeature = (feature) => {
    setFormData(prev => ({
      ...prev,
      trip_features: prev.trip_features.includes(feature)
        ? prev.trip_features.filter(f => f !== feature)
        : [...prev.trip_features, feature]
    }));
  };
  
  // Toggle month
  const toggleMonth = (month) => {
    setFormData(prev => ({
      ...prev,
      preferred_months: prev.preferred_months.includes(month)
        ? prev.preferred_months.filter(m => m !== month)
        : [...prev.preferred_months, month]
    }));
  };
  
  // Resize image before upload
  const resizeImage = (file, maxWidth = 1920, maxHeight = 1080) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = document.createElement('img');
        img.onload = () => {
          // Calculate new dimensions
          let width = img.width;
          let height = img.height;
          
          // Only resize if image is larger than max dimensions
          if (width > maxWidth || height > maxHeight) {
            const aspectRatio = width / height;
            
            if (width > height) {
              width = Math.min(width, maxWidth);
              height = width / aspectRatio;
            } else {
              height = Math.min(height, maxHeight);
              width = height * aspectRatio;
            }
          }
          
          // Create canvas and resize
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob
          canvas.toBlob((blob) => {
            if (blob) {
              // Create a new file with the same name
              const resizedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              resolve(resizedFile);
            } else {
              reject(new Error('Failed to resize image'));
            }
          }, 'image/jpeg', 0.85); // 85% quality for good balance
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };
  
  // Process and upload image
  const processAndUploadImage = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      console.error('Invalid file type');
      return;
    }
    
    setUploadingImage(true);
    
    try {
      // Resize image if needed
      console.log('Original file size:', (file.size / 1024 / 1024).toFixed(2), 'MB');
      const resizedFile = await resizeImage(file);
      console.log('Resized file size:', (resizedFile.size / 1024 / 1024).toFixed(2), 'MB');
      
      // Upload resized image
      const result = await supabaseService.uploadImage(resizedFile, 'single-family');
      
      if (result && result.url) {
        setFormData(prev => ({
          ...prev,
          images: {
            ...prev.images,
            [currentImageSlot]: result.url
          }
        }));
      } else {
        throw new Error('Upload failed - no URL returned');
      }
    } catch (error) {
      console.error('Error processing/uploading image:', error);
      // Fallback to local blob URL if upload fails
      const localUrl = URL.createObjectURL(file);
      setFormData(prev => ({
        ...prev,
        images: {
          ...prev.images,
          [currentImageSlot]: localUrl
        }
      }));
      alert('Image upload failed. Using local preview. The image may not be saved.');
    } finally {
      setUploadingImage(false);
    }
  };
  
  // Handle image upload from file input
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (file) {
      await processAndUploadImage(file);
    }
  };
  
  // Handle paste event
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            // Find the first empty slot or use the current slot
            const emptySlot = ['main', 'secondary', 'tertiary'].find(
              slot => !formData.images[slot]
            );
            if (emptySlot) {
              setCurrentImageSlot(emptySlot);
              processAndUploadImage(file);
              e.preventDefault();
              break;
            }
          }
        }
      }
    }
  };
  
  // Handle drag events
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0 && files[0].type.startsWith('image/')) {
      // Find the first empty slot
      const emptySlot = ['main', 'secondary', 'tertiary'].find(
        slot => !formData.images[slot]
      );
      if (emptySlot) {
        setCurrentImageSlot(emptySlot);
        processAndUploadImage(files[0]);
      }
    }
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };
  
  // Handle budget update from calculator
  const handleBudgetUpdate = (budgetData) => {
    setFormData(prev => ({
      ...prev,
      budget_breakdown: budgetData
    }));
  };
  
  // Accommodation management
  const addAccommodation = (type = 'hotel') => {
    const newAccommodation = {
      id: Date.now(), // Temporary ID for form management
      name: '',
      type: type,
      price_per_night: 0,
      rooms_needed: 1,
      url: '',
      description: '',
      images: []
    };
    setFormData(prev => ({
      ...prev,
      accommodations: [...prev.accommodations, newAccommodation]
    }));
  };
  
  const updateAccommodation = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      accommodations: prev.accommodations.map((acc, i) => 
        i === index ? { ...acc, [field]: value } : acc
      )
    }));
  };
  
  const removeAccommodation = (index) => {
    setFormData(prev => ({
      ...prev,
      accommodations: prev.accommodations.filter((_, i) => i !== index)
    }));
  };
  
  // Activity management
  const addActivity = (priority = 'must') => {
    const newActivity = {
      id: Date.now(),
      title: '',
      description: '',
      priority: priority,
      cost_estimate: 0,
      url: '',
      images: []
    };
    setFormData(prev => ({
      ...prev,
      trip_activities: [...prev.trip_activities, newActivity]
    }));
  };
  
  const updateActivity = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      trip_activities: prev.trip_activities.map((act, i) => 
        i === index ? { ...act, [field]: value } : act
      )
    }));
  };
  
  const removeActivity = (index) => {
    setFormData(prev => ({
      ...prev,
      trip_activities: prev.trip_activities.filter((_, i) => i !== index)
    }));
  };
  
  // Handle accommodation image upload
  const handleAccommodationImage = async (fileOrEvent, accIndex, imageIndex) => {
    const file = fileOrEvent.target ? fileOrEvent.target.files[0] : fileOrEvent;
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const resizedFile = await resizeImage(file, 1920, 1080);
      // Use same upload pattern as main images
      const result = await supabaseService.uploadImage(resizedFile, 'single-family');
      
      if (result && result.url) {
        const newImages = [...(formData.accommodations[accIndex].images || [])];
        newImages[imageIndex] = result.url;  // Use result.url, not the whole object
        updateAccommodation(accIndex, 'images', newImages);
      } else {
        throw new Error('Upload failed - no URL returned');
      }
    } catch (error) {
      console.error('Error uploading accommodation image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };
  
  // Handle activity image upload
  const handleActivityImage = async (fileOrEvent, actIndex, imageIndex) => {
    const file = fileOrEvent.target ? fileOrEvent.target.files[0] : fileOrEvent;
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const resizedFile = await resizeImage(file, 1920, 1080);
      // Use same upload pattern as main images
      const result = await supabaseService.uploadImage(resizedFile, 'single-family');
      
      if (result && result.url) {
        const newImages = [...(formData.trip_activities[actIndex].images || [])];
        newImages[imageIndex] = result.url;  // Use result.url, not the whole object
        updateActivity(actIndex, 'images', newImages);
      } else {
        throw new Error('Upload failed - no URL returned');
      }
    } catch (error) {
      console.error('Error uploading activity image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };
  
  // Track which field has focus for paste operations
  const [pasteTarget, setPasteTarget] = useState({ type: null, index: null, imageIndex: null });
  
  // Calculate trip duration in days for budget calculator
  const getTripDurationDays = () => {
    const durationMap = {
      'overnight': 1,
      'weekend': 3,
      '4-7days': 5,
      '1-2weeks': 10,
      '2+weeks': 14
    };
    return durationMap[formData.duration_category] || 3;
  };
  
  // Submit form
  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      alert('Please enter a trip title');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const tripData = {
        title: formData.title,
        location: formData.location,
        description: formData.description,
        category: 'travel-trips',
        activity_type: 'trip',
        trip_metadata: {
          transport_type: formData.transport_type,
          duration_category: formData.duration_category,
          travel_time: formData.travel_time,
          trip_features: formData.trip_features,
          preferred_season: formData.preferred_season,
          preferred_months: formData.preferred_months,
          images: formData.images,
          budget_breakdown: formData.budget_breakdown,
          trip_phase: formData.trip_phase,
          passport_required: formData.passport_required,
          visa_required: formData.visa_required,
          interested_members: [],
          rooms_needed: Math.ceil(numberOfPeople / 2),
          accommodations: formData.accommodations,
          trip_activities: formData.trip_activities
        },
        status: 'idea',
        created_by: currentUser?.id
      };
      
      if (mode === 'edit' && trip?.id) {
        await updateFamilyActivity(trip.id, tripData);
      } else {
        await addFamilyActivity(tripData, currentUser?.id);
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving trip:', error);
      alert('Failed to save trip. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 0: // Basics
        return (
          <div className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Trip Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
                placeholder="e.g., Disney World Adventure, Beach Getaway, Mountain Retreat"
              />
            </div>
            
            {/* Destination */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Destination
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  className="w-full pl-10 pr-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
                  placeholder="e.g., Orlando, Florida or Yellowstone National Park"
                />
              </div>
            </div>
            
            {/* Transport Type */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                How will you get there?
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {transportOptions.map(option => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.value}
                      onClick={() => setFormData(prev => ({ ...prev, transport_type: option.value }))}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        formData.transport_type === option.value
                          ? 'border-gold-500 bg-gold-50 text-gold-900'
                          : 'border-stone-200 hover:border-stone-300'
                      }`}
                    >
                      <Icon className="w-6 h-6 mx-auto mb-1" />
                      <span className="text-sm font-medium">{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Travel Time */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                Travel Time (one way)
              </label>
              <input
                type="text"
                value={formData.travel_time}
                onChange={(e) => setFormData(prev => ({ ...prev, travel_time: e.target.value }))}
                className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
                placeholder="e.g., 2 hours drive, 3.5 hour flight, 1 day road trip"
              />
              <p className="text-xs text-stone-500 mt-1">
                How long does it take to get there from home?
              </p>
            </div>
            
            {/* Duration */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Trip Duration
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {durationOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setFormData(prev => ({ ...prev, duration_category: option.value }))}
                    className={`px-4 py-2 rounded-lg border-2 transition-all ${
                      formData.duration_category === option.value
                        ? 'border-gold-500 bg-gold-50 text-gold-900'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <span className="text-sm font-medium">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Description / Notes
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
                rows={3}
                placeholder="What's the vision for this trip? Any special things you want to do or see?"
              />
            </div>
          </div>
        );
        
      case 1: // Details
        return (
          <div className="space-y-6">
            {/* Trip Features */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Trip Features (select all that apply)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {tripFeatures.map(feature => (
                  <button
                    key={feature.value}
                    onClick={() => toggleFeature(feature.value)}
                    className={`px-3 py-2 rounded-lg border text-left transition-all flex items-center gap-2 ${
                      formData.trip_features.includes(feature.value)
                        ? 'border-gold-500 bg-gold-50 text-gold-900'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <span className="text-lg">{feature.icon}</span>
                    <span className="text-sm">{feature.label}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Preferred Season */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Best Time to Go
              </label>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mb-4">
                {seasons.map(season => (
                  <button
                    key={season}
                    onClick={() => setFormData(prev => ({ ...prev, preferred_season: season.toLowerCase() }))}
                    className={`px-3 py-2 rounded-lg border transition-all ${
                      formData.preferred_season === season.toLowerCase()
                        ? 'border-gold-500 bg-gold-50 text-gold-900'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <span className="text-sm">{season}</span>
                  </button>
                ))}
              </div>
              
              {/* Preferred Months */}
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Ideal Months (optional)
              </label>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                {months.map(month => (
                  <button
                    key={month}
                    onClick={() => toggleMonth(month)}
                    className={`px-2 py-1 rounded-lg border text-xs transition-all ${
                      formData.preferred_months.includes(month)
                        ? 'border-gold-500 bg-gold-50 text-gold-900'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    {month.slice(0, 3)}
                  </button>
                ))}
              </div>
            </div>
            
            {/* International Requirements */}
            {formData.trip_features.includes('international') && (
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  International Travel Requirements
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.passport_required}
                      onChange={(e) => setFormData(prev => ({ ...prev, passport_required: e.target.checked }))}
                      className="rounded border-stone-300 text-gold-500 focus:ring-gold-500"
                    />
                    <span className="text-sm">Passports required</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.visa_required}
                      onChange={(e) => setFormData(prev => ({ ...prev, visa_required: e.target.checked }))}
                      className="rounded border-stone-300 text-gold-500 focus:ring-gold-500"
                    />
                    <span className="text-sm">Visas required</span>
                  </label>
                </div>
              </div>
            )}
            
            {/* Images */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Trip Images (up to 3) - Paste (Cmd/Ctrl+V), drag or click to upload
              </label>
              <div className={`grid grid-cols-3 gap-3 ${dragOver ? 'ring-2 ring-gold-500 ring-offset-2 rounded-lg' : ''}`}>
                {['main', 'secondary', 'tertiary'].map((slot, index) => (
                  <div key={slot} className="relative">
                    {formData.images[slot] ? (
                      <div className="relative group">
                        <img
                          src={formData.images[slot]}
                          alt={`Trip ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            images: { ...prev.images, [slot]: null }
                          }))}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setCurrentImageSlot(slot);
                          fileInputRef.current?.click();
                        }}
                        disabled={uploadingImage}
                        className="w-full h-24 border-2 border-dashed border-stone-300 rounded-lg hover:border-gold-500 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {uploadingImage && currentImageSlot === slot ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gold-500" />
                        ) : (
                          <Upload className="w-5 h-5 text-stone-400" />
                        )}
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>
          </div>
        );
        
      case 2: // Lodging Ideas
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-stone-900">Accommodation Options</h3>
                <p className="text-sm text-stone-600 mt-1">Add potential places to stay during your trip</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => addAccommodation('hotel')}
                  className="px-3 py-1.5 text-sm bg-azure-50 text-azure-700 rounded-lg hover:bg-azure-100 transition-colors flex items-center gap-1"
                >
                  <Hotel className="w-4 h-4" />
                  Add Hotel
                </button>
                <button
                  onClick={() => addAccommodation('rental')}
                  className="px-3 py-1.5 text-sm bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors flex items-center gap-1"
                >
                  <Home className="w-4 h-4" />
                  Add Rental
                </button>
              </div>
            </div>
            
            {formData.accommodations.length === 0 ? (
              <div className="text-center py-12 bg-stone-50 rounded-xl">
                <Hotel className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                <p className="text-stone-600">No accommodations added yet</p>
                <p className="text-sm text-stone-500 mt-1">Click "Add Hotel" or "Add Rental" to get started</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {formData.accommodations.map((acc, index) => (
                  <div key={acc.id} className="border border-stone-200 rounded-lg p-4 bg-white">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {acc.type === 'hotel' ? <Hotel className="w-5 h-5 text-azure-600" /> : <Home className="w-5 h-5 text-emerald-600" />}
                        <span className="font-medium text-stone-900 capitalize">{acc.type} Option {index + 1}</span>
                      </div>
                      <button
                        onClick={() => removeAccommodation(index)}
                        className="p-1 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                    
                    {/* Name */}
                    <div className="mb-3">
                      <input
                        type="text"
                        value={acc.name}
                        onChange={(e) => updateAccommodation(index, 'name', e.target.value)}
                        className="w-full px-3 py-1.5 text-sm border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
                        placeholder="e.g., Hilton Downtown, Cozy Airbnb"
                      />
                    </div>
                    
                    {/* Price and Rooms */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div>
                        <label className="text-xs text-stone-600">$/night</label>
                        <input
                          type="number"
                          value={acc.price_per_night}
                          onChange={(e) => updateAccommodation(index, 'price_per_night', e.target.value)}
                          className="w-full px-3 py-1.5 text-sm border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
                          placeholder="150"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-stone-600">Rooms</label>
                        <input
                          type="number"
                          value={acc.rooms_needed}
                          onChange={(e) => updateAccommodation(index, 'rooms_needed', e.target.value)}
                          className="w-full px-3 py-1.5 text-sm border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
                          placeholder="2"
                          min="1"
                        />
                      </div>
                    </div>
                    
                    {/* Images */}
                    <div className="mb-3">
                      <label className="text-xs text-stone-600 mb-1 block">Photos (up to 3)</label>
                      <div className="flex gap-2">
                        {[0, 1, 2].map((imgIndex) => (
                          <div 
                            key={imgIndex} 
                            className={`relative w-24 h-16 bg-stone-100 rounded-lg overflow-hidden border-2 transition-colors ${
                              pasteTarget.type === 'accommodation' && pasteTarget.index === index && pasteTarget.imageIndex === imgIndex
                                ? 'border-gold-500 ring-2 ring-gold-200'
                                : 'border-stone-200'
                            }`}
                            onFocus={() => setPasteTarget({ type: 'accommodation', index, imageIndex: imgIndex })}
                            onMouseEnter={() => setPasteTarget({ type: 'accommodation', index, imageIndex: imgIndex })}
                            tabIndex={0}
                          >
                            {acc.images?.[imgIndex] ? (
                              <>
                                <img
                                  src={acc.images[imgIndex]}
                                  alt={`${acc.name} ${imgIndex + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                <button
                                  onClick={() => {
                                    const newImages = [...(acc.images || [])];
                                    newImages[imgIndex] = null;
                                    updateAccommodation(index, 'images', newImages.filter(Boolean));
                                  }}
                                  className="absolute top-0.5 right-0.5 p-0.5 bg-red-500 text-white rounded hover:bg-red-600"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </>
                            ) : (
                              <label 
                                className="flex items-center justify-center w-full h-full cursor-pointer hover:bg-stone-200 transition-colors"
                                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('bg-stone-300'); }}
                                onDragLeave={(e) => { e.currentTarget.classList.remove('bg-stone-300'); }}
                                onDrop={async (e) => {
                                  e.preventDefault();
                                  e.currentTarget.classList.remove('bg-stone-300');
                                  const file = e.dataTransfer.files[0];
                                  if (file && file.type.startsWith('image/')) {
                                    await handleAccommodationImage(file, index, imgIndex);
                                  }
                                }}
                              >
                                <Image className="w-5 h-5 text-stone-400" />
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handleAccommodationImage(e, index, imgIndex)}
                                  className="hidden"
                                />
                              </label>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Description */}
                    <div className="mb-3">
                      <textarea
                        value={acc.description}
                        onChange={(e) => updateAccommodation(index, 'description', e.target.value)}
                        className="w-full px-3 py-1.5 text-sm border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
                        placeholder="Brief description or notes..."
                        rows="2"
                      />
                    </div>
                    
                    {/* URL */}
                    <div>
                      <input
                        type="url"
                        value={acc.url}
                        onChange={(e) => updateAccommodation(index, 'url', e.target.value)}
                        className="w-full px-3 py-1.5 text-sm border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
                        placeholder="Booking link (optional)"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
        
      case 3: // Activity Ideas
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-stone-900">Things to Do</h3>
                <p className="text-sm text-stone-600 mt-1">Add activities and attractions for your trip</p>
              </div>
              <button
                onClick={() => addActivity('must')}
                className="px-3 py-1.5 text-sm bg-violet-50 text-violet-700 rounded-lg hover:bg-violet-100 transition-colors flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Activity
              </button>
            </div>
            
            {formData.trip_activities.length === 0 ? (
              <div className="text-center py-12 bg-stone-50 rounded-xl">
                <Ticket className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                <p className="text-stone-600">No activities added yet</p>
                <p className="text-sm text-stone-500 mt-1">Click "Add Activity" to get started</p>
              </div>
            ) : (
              <>
                {/* Must See Activities */}
                <div>
                  <h4 className="text-sm font-medium text-stone-700 mb-3 flex items-center gap-2">
                    <span className="text-orange-500">★</span> Must-See Attractions
                  </h4>
                  <div className="grid gap-4 md:grid-cols-2">
                    {formData.trip_activities.filter(a => a.priority === 'must').map((activity, index) => {
                      const actualIndex = formData.trip_activities.findIndex(a => a.id === activity.id);
                      return (
                        <div key={activity.id} className="border border-orange-200 rounded-lg p-4 bg-orange-50/50">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Ticket className="w-5 h-5 text-orange-600" />
                              <span className="font-medium text-stone-900">Must See</span>
                            </div>
                            <button
                              onClick={() => removeActivity(actualIndex)}
                              className="p-1 hover:bg-red-50 rounded transition-colors"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          </div>
                          
                          {/* Title */}
                          <div className="mb-3">
                            <input
                              type="text"
                              value={activity.title}
                              onChange={(e) => updateActivity(actualIndex, 'title', e.target.value)}
                              className="w-full px-3 py-1.5 text-sm border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
                              placeholder="Activity name"
                            />
                          </div>
                          
                          {/* Cost and Priority */}
                          <div className="grid grid-cols-2 gap-2 mb-3">
                            <div>
                              <label className="text-xs text-stone-600">Est. Cost</label>
                              <input
                                type="number"
                                value={activity.cost_estimate}
                                onChange={(e) => updateActivity(actualIndex, 'cost_estimate', e.target.value)}
                                className="w-full px-3 py-1.5 text-sm border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
                                placeholder="50"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-stone-600">Priority</label>
                              <select
                                value={activity.priority}
                                onChange={(e) => updateActivity(actualIndex, 'priority', e.target.value)}
                                className="w-full px-3 py-1.5 text-sm border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
                              >
                                <option value="must">Must See</option>
                                <option value="optional">If Time</option>
                              </select>
                            </div>
                          </div>
                          
                          {/* Images */}
                          <div className="mb-3">
                            <label className="text-xs text-stone-600 mb-1 block">Photos (up to 3)</label>
                            <div className="flex gap-2">
                              {[0, 1, 2].map((imgIndex) => (
                                <div 
                                  key={imgIndex} 
                                  className={`relative w-20 h-14 bg-stone-100 rounded overflow-hidden border-2 transition-colors ${
                                    pasteTarget.type === 'activity' && pasteTarget.index === actualIndex && pasteTarget.imageIndex === imgIndex
                                      ? 'border-gold-500 ring-2 ring-gold-200'
                                      : 'border-stone-200'
                                  }`}
                                  onFocus={() => setPasteTarget({ type: 'activity', index: actualIndex, imageIndex: imgIndex })}
                                  onMouseEnter={() => setPasteTarget({ type: 'activity', index: actualIndex, imageIndex: imgIndex })}
                                  tabIndex={0}
                                >
                                  {activity.images?.[imgIndex] ? (
                                    <>
                                      <img
                                        src={activity.images[imgIndex]}
                                        alt={`${activity.title} ${imgIndex + 1}`}
                                        className="w-full h-full object-cover"
                                      />
                                      <button
                                        onClick={() => {
                                          const newImages = [...(activity.images || [])];
                                          newImages[imgIndex] = null;
                                          updateActivity(actualIndex, 'images', newImages.filter(Boolean));
                                        }}
                                        className="absolute top-0.5 right-0.5 p-0.5 bg-red-500 text-white rounded hover:bg-red-600"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </>
                                  ) : (
                                    <label 
                                      className="flex items-center justify-center w-full h-full cursor-pointer hover:bg-stone-200 transition-colors"
                                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('bg-stone-300'); }}
                                      onDragLeave={(e) => { e.currentTarget.classList.remove('bg-stone-300'); }}
                                      onDrop={async (e) => {
                                        e.preventDefault();
                                        e.currentTarget.classList.remove('bg-stone-300');
                                        const file = e.dataTransfer.files[0];
                                        if (file && file.type.startsWith('image/')) {
                                          await handleActivityImage(file, actualIndex, imgIndex);
                                        }
                                      }}
                                    >
                                      <Image className="w-4 h-4 text-stone-400" />
                                      <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleActivityImage(e, actualIndex, imgIndex)}
                                        className="hidden"
                                      />
                                    </label>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          {/* Description */}
                          <div>
                            <textarea
                              value={activity.description}
                              onChange={(e) => updateActivity(actualIndex, 'description', e.target.value)}
                              className="w-full px-3 py-1.5 text-sm border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
                              placeholder="Brief description..."
                              rows="2"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Optional Activities */}
                {formData.trip_activities.filter(a => a.priority === 'optional').length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-stone-700 mb-3 flex items-center gap-2">
                      <span className="text-azure-500">◆</span> If Time Allows
                    </h4>
                    <div className="grid gap-4 md:grid-cols-2">
                      {formData.trip_activities.filter(a => a.priority === 'optional').map((activity, index) => {
                        const actualIndex = formData.trip_activities.findIndex(a => a.id === activity.id);
                        return (
                          <div key={activity.id} className="border border-azure-200 rounded-lg p-4 bg-azure-50/50">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <Ticket className="w-5 h-5 text-azure-600" />
                                <span className="font-medium text-stone-900">Optional</span>
                              </div>
                              <button
                                onClick={() => removeActivity(actualIndex)}
                                className="p-1 hover:bg-red-50 rounded transition-colors"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </button>
                            </div>
                            
                            {/* Same fields as Must See */}
                            <div className="mb-3">
                              <input
                                type="text"
                                value={activity.title}
                                onChange={(e) => updateActivity(actualIndex, 'title', e.target.value)}
                                className="w-full px-3 py-1.5 text-sm border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
                                placeholder="Activity name"
                              />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2 mb-3">
                              <div>
                                <label className="text-xs text-stone-600">Est. Cost</label>
                                <input
                                  type="number"
                                  value={activity.cost_estimate}
                                  onChange={(e) => updateActivity(actualIndex, 'cost_estimate', e.target.value)}
                                  className="w-full px-3 py-1.5 text-sm border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
                                  placeholder="50"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-stone-600">Priority</label>
                                <select
                                  value={activity.priority}
                                  onChange={(e) => updateActivity(actualIndex, 'priority', e.target.value)}
                                  className="w-full px-3 py-1.5 text-sm border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
                                >
                                  <option value="must">Must See</option>
                                  <option value="optional">If Time</option>
                                </select>
                              </div>
                            </div>
                            
                            <div className="mb-3">
                              <label className="text-xs text-stone-600 mb-1 block">Photos (up to 3)</label>
                              <div className="flex gap-2">
                                {[0, 1, 2].map((imgIndex) => (
                                  <div 
                                    key={imgIndex} 
                                    className={`relative w-20 h-14 bg-stone-100 rounded overflow-hidden border-2 transition-colors ${
                                      pasteTarget.type === 'activity' && pasteTarget.index === actualIndex && pasteTarget.imageIndex === imgIndex
                                        ? 'border-gold-500 ring-2 ring-gold-200'
                                        : 'border-stone-200'
                                    }`}
                                    onFocus={() => setPasteTarget({ type: 'activity', index: actualIndex, imageIndex: imgIndex })}
                                    onMouseEnter={() => setPasteTarget({ type: 'activity', index: actualIndex, imageIndex: imgIndex })}
                                    tabIndex={0}
                                  >
                                    {activity.images?.[imgIndex] ? (
                                      <>
                                        <img
                                          src={activity.images[imgIndex]}
                                          alt={`${activity.title} ${imgIndex + 1}`}
                                          className="w-full h-full object-cover"
                                        />
                                        <button
                                          onClick={() => {
                                            const newImages = [...(activity.images || [])];
                                            newImages[imgIndex] = null;
                                            updateActivity(actualIndex, 'images', newImages.filter(Boolean));
                                          }}
                                          className="absolute top-0.5 right-0.5 p-0.5 bg-red-500 text-white rounded hover:bg-red-600"
                                        >
                                          <X className="w-3 h-3" />
                                        </button>
                                      </>
                                    ) : (
                                      <label 
                                        className="flex items-center justify-center w-full h-full cursor-pointer hover:bg-stone-200 transition-colors"
                                        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('bg-stone-300'); }}
                                        onDragLeave={(e) => { e.currentTarget.classList.remove('bg-stone-300'); }}
                                        onDrop={async (e) => {
                                          e.preventDefault();
                                          e.currentTarget.classList.remove('bg-stone-300');
                                          const file = e.dataTransfer.files[0];
                                          if (file && file.type.startsWith('image/')) {
                                            await handleActivityImage(file, actualIndex, imgIndex);
                                          }
                                        }}
                                      >
                                        <Image className="w-4 h-4 text-stone-400" />
                                        <input
                                          type="file"
                                          accept="image/*"
                                          onChange={(e) => handleActivityImage(e, actualIndex, imgIndex)}
                                          className="hidden"
                                        />
                                      </label>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            <div>
                              <textarea
                                value={activity.description}
                                onChange={(e) => updateActivity(actualIndex, 'description', e.target.value)}
                                className="w-full px-3 py-1.5 text-sm border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
                                placeholder="Brief description..."
                                rows="2"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );
        
      case 4: // Budget
        return (
          <div>
            <TripBudgetCalculator
              initialBudget={formData.budget_breakdown}
              tripDuration={getTripDurationDays()}
              numberOfPeople={numberOfPeople}
              onBudgetUpdate={handleBudgetUpdate}
            />
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div 
        className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onPaste={async (e) => {
          const items = e.clipboardData?.items;
          if (!items) return;
          
          for (let item of items) {
            if (item.type.indexOf('image') !== -1) {
              e.preventDefault();
              const file = item.getAsFile();
              if (!file) continue;
              
              // Check if we have a specific paste target
              if (pasteTarget.type === 'accommodation' && pasteTarget.index !== null && pasteTarget.imageIndex !== null) {
                await handleAccommodationImage(file, pasteTarget.index, pasteTarget.imageIndex);
                // Clear the paste target after use
                setPasteTarget({ type: null, index: null, imageIndex: null });
              } else if (pasteTarget.type === 'activity' && pasteTarget.index !== null && pasteTarget.imageIndex !== null) {
                await handleActivityImage(file, pasteTarget.index, pasteTarget.imageIndex);
                // Clear the paste target after use
                setPasteTarget({ type: null, index: null, imageIndex: null });
              } else {
                // Default to main trip images
                handlePaste(e);
              }
              break;
            }
          }
        }}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-stone-900 flex items-center gap-2">
            <Plane className="w-6 h-6 text-azure-600" />
            {mode === 'edit' ? 'Edit Trip' : 'Plan a Family Trip'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Tab Navigation */}
        <div className="px-6 py-3 border-b border-stone-200">
          <div className="flex gap-4 overflow-x-auto">
            {['Basics', 'Details', 'Lodging Ideas', 'Activity Ideas', 'Budget'].map((tab, index) => (
              <button
                key={tab}
                onClick={() => setActiveTab(index)}
                className={`px-4 py-2 font-medium transition-all ${
                  activeTab === index
                    ? 'text-gold-600 border-b-2 border-gold-600'
                    : 'text-stone-600 hover:text-stone-900'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {renderTabContent()}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-stone-200 flex items-center justify-between">
          <div className="flex gap-2">
            {activeTab > 0 && (
              <button
                onClick={() => setActiveTab(prev => prev - 1)}
                className="px-4 py-2 text-stone-600 hover:text-stone-900 transition-colors flex items-center gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
            )}
          </div>
          
          <div className="flex gap-2">
            {activeTab < 2 ? (
              <button
                onClick={() => setActiveTab(prev => prev + 1)}
                className="px-4 py-2 bg-gold-500 text-white rounded-lg hover:bg-gold-600 transition-colors flex items-center gap-2"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-6 py-2 bg-gold-500 text-white rounded-lg hover:bg-gold-600 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {isSubmitting ? 'Saving...' : (mode === 'edit' ? 'Update Trip' : 'Save Trip')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}